import React, { useState } from "react";
import { Field, Form } from "react-final-form";
import ValidatedInput from "../../common/ValidatedInput";
import { getCountries, validateVat } from "../../../queries/organization";
import PropTypes from "prop-types";
import isEmpty from "lodash/isEmpty";
import { OnChange } from "react-final-form-listeners";
import CustomSelect from "../../common/CustomSelect";
import { useQuery } from "react-query";

const OrganizationForm = ({ onSubmit, organization }) => {
  const [vatValidationError, setVatValidationError] = useState("");
  const [vatResponse, setVatResponse] = useState(null);
  const {
    isLoading,
    isError,
    data: countries,
  } = useQuery(
    "countries",
    () =>
      getCountries().then((data) => {
        return data;
      }),
    {
      keepPreviousData: true,
    }
  );

  const handleVatValidation = async (value) => {
    if (!isEmpty(value)) {
      await validateVat({ vat: value })
        .then((data) => {
          setVatValidationError("");
          setVatResponse(data);
        })
        .catch((error) => {
          setVatValidationError(
            `Validation failed ${error.response.data.errors[0].detail}`
          );
          return error;
        });
    } else {
      setVatValidationError("");
    }
  };

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={organization}
      validate={(values) => {
        const errors = {};
        if (!values.legalName) {
          errors.legalName = "Legal Name is Required";
        }
        if (!values.vat) {
          errors.vat = "VAT number is Required";
        }
        if (!values.billingAddress) {
          errors.billingAddress = "Address is Required";
        }
        if (values.email) {
          if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.email)) {
            errors.email = "Invalid email address";
          }
        }
        return errors;
      }}
      render={({
        submitError,
        handleSubmit,
        form,
        submitting,
        pristine,
        values,
      }) => (
        <form onSubmit={handleSubmit}>
          <h5 className="mt-5 mb-4">Organization Information</h5>
          {submitError && (
            <p className="text-center text-error">{submitError} </p>
          )}
          {vatValidationError && (
            <strong style={{ color: "red" }}>{vatValidationError}</strong>
          )}
          <Field
            label={"VAT No."}
            inputClassName="fin-control"
            type="text"
            name="vat"
            parse={(value) => value.trim()}
            component={ValidatedInput}
            placeholder="eg EL101256987"
          />
          <OnChange name="vat">
            {(value, previous) => {
              if (value !== previous) handleVatValidation(value);
            }}
          </OnChange>
          {!isEmpty(vatResponse) && (
            <p className="text-description">
              Verified data for<strong> {vatResponse?.vat}</strong>
              <span className="d-inline-block">
                Legal Name: <strong>{vatResponse?.legalName}</strong>,
              </span>{" "}
              <span className="d-inline-block">
                Address: <strong>{vatResponse?.billingAddress}</strong>,
              </span>{" "}
              <span className="d-inline-block">
                Country: <strong>{vatResponse?.country}</strong>
              </span>
            </p>
          )}
          <Field
            label={"Organization name"}
            inputClassName="fin-control"
            component={ValidatedInput}
            name="legalName"
            type="text"
            placeholder="eg Finclude"
          />
          <h5 className="mt-5 mb-4">Billing Information</h5>
          <Field
            label={"Address"}
            inputClassName="fin-control"
            name="billingAddress"
            type="text"
            component={ValidatedInput}
            placeholder="Address"
          />
          <div className="row row-xs">
            <div className="col-7">
              <Field
                label={"City"}
                inputClassName="fin-control"
                name="city"
                component={ValidatedInput}
                type="text"
                placeholder="City"
              />
            </div>
            <div className="col-5">
              <Field
                label={"ZIP Code"}
                inputClassName="fin-control"
                name="postalCode"
                component={ValidatedInput}
                type="text"
                placeholder="Zip code"
              />
            </div>
          </div>
          {countries && !isLoading && !isError && (
            <Field
              label={"Country"}
              options={countries}
              name="country"
              component={CustomSelect}
              placeholder="Country"
              isSearchable={true}
              isMulti={false}
              isClearable={true}
            />
          )}
          <h5 className="mt-5 mb-4">Admin assignment</h5>
          {!isEmpty(organization?.creator) && (
            <div className="fin-col">
              <>
                <h6 className="mt-1 mb-1">Organization Creator</h6>
                <p className="text-description">
                  <span className="d-inline-block">
                    Full name: <strong>{organization.creator.full_name}</strong>
                  </span>{" "}
                  <br />
                  <span className="d-inline-block">
                    Email: <strong>{organization.creator.email}</strong>
                  </span>
                </p>
              </>
            </div>
          )}

          <div className="fin-col">
            <label>Admin A</label>
            <Field
              inputClassName="fin-control"
              name="adminA"
              component={ValidatedInput}
              type="email"
              placeholder="Email"
            />
          </div>
          <div className="fin-col">
            <label>Admin B</label>
            <Field
              inputClassName="fin-control"
              name="adminB"
              component={ValidatedInput}
              type="email"
              placeholder="Email"
            />
          </div>

          <div className="mt-45">
            <button
              className="btn btn-sm btn-main"
              disabled={submitting || pristine}
            >
              {isEmpty(organization)
                ? `Create organization`
                : "Update organization"}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-cancel"
              onClick={form.reset}
              disabled={submitting || pristine}
            >
              Reset
            </button>
          </div>
        </form>
      )}
    />
  );
};

OrganizationForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  organization: PropTypes.object,
};

export default OrganizationForm;
